import { type IBooleanCondition } from '../../inputs/expressions-editor/conditions/IBooleanCondition.tsx';
import { type IParentWidgetScopeItem } from '../widgets/IWidgetType.ts';
import { type IRootWidget } from '../widgets/repository/containers/root/IRootWidget.ts';

/**
 * Classificazione di una sezione condivisa (`tipoCompilazione === PER_DOMANDA`).
 *
 * - `owned`    → la sezione appartiene al modulo corrente (editabile)
 * - `external` → la sezione proviene da un altro modulo (readonly);
 *                `moduleId` indica il modulo proprietario originale
 *
 * Se la sezione non è condivisa il campo `shared` è `null` o `undefined`.
 */
export type IEngineSectionShared =
  | { type: 'owned' }
  | { type: 'external'; moduleId: string };

/**
 * Sezione astratta per essere utilizata all'interno del motore di esecuzione
 * dei form.
 */
export interface IEngineSection {
  id: string;
  ultimaVersione:
    | {
        /**
         * ID della versione della sezione. Serve per capire se la sezione è più
         * aggiornata rispetto a quella eventualmente in uso nel form e/o per la
         * cache dello schema di validazione.
         *
         * Se viene lasciato `null` o `undefined`, si intende che la versione non è
         * definita, e quindi non si può fare il confronto (non è possibile
         * ad esempio cachare lo schema di validazione).
         */
        versionId: string | null | undefined;

        elementi: IRootWidget | null | undefined;
        visibile: IBooleanCondition | null | undefined;
        compilabile: IBooleanCondition | null | undefined;
      }
    | null
    | undefined;
  /**
   * Gestisce il ParentScope della _singola_ sezione.
   */
  sectionRootScope?: Partial<IParentWidgetScopeItem>;
  /**
   * Informazione sulla condivisione della sezione.
   * Impostato durante la fase di mappatura (FE: `useSections`, BE: `convertSectionsToEngine`).
   *
   * - `null` / `undefined` → sezione non condivisa
   * - `{ type: 'owned' }`  → sezione condivisa di proprietà del modulo corrente
   * - `{ type: 'external', moduleId }` → sezione condivisa proveniente da un altro modulo
   */
  shared?: IEngineSectionShared | null;
}

export function mapToEngineSection(input: {
  id?: string | null;
  versionId?: string | null;
  elementi?: any;
  visibile?: any;
  compilabile?: any;
  sectionRootScope?: Partial<IParentWidgetScopeItem>;
}): IEngineSection {
  return {
    id: input.id ?? '',
    ultimaVersione: {
      versionId: input.versionId ?? '',
      elementi: input.elementi,
      // Questi campi sono opzionali, e non vengono passati per l'utilizzo
      // attuale (ovvero per la verifica di una nuova versione).
      // Essendo presenti solo nelle `regoleAbilitazione` del modulo-sezione, non
      // sono mai presenti in questa verifica.
      visibile: input.visibile ?? undefined,
      compilabile: input.compilabile ?? undefined,
    },
    sectionRootScope: input.sectionRootScope,
  };
}

// ---------------------------------------------------------------------------
// Utility per le sezioni condivise
// ---------------------------------------------------------------------------

/**
 * Calcola il valore `shared` per una singola sezione.
 *
 * Logica centralizzata utilizzata sia dal FE (`useSections`) che dal
 * BE (`convertSectionsToEngine`) in fase di mappatura.
 *
 * @param isSharedSection  `true` se la sezione è di tipo `PER_DOMANDA`
 * @param requestModuleId  ID del modulo richiesta corrente
 * @param sourceModuleId   `idModuloRichiestaSorgente` dal valore della sezione
 */
export function computeSectionShared(params: {
  isSharedSection: boolean;
  requestModuleId: string | null | undefined;
  sourceModuleId: string | null | undefined;
}): IEngineSectionShared | null {
  if (!params.isSharedSection) return null;

  if (
    params.sourceModuleId &&
    params.requestModuleId &&
    params.sourceModuleId !== params.requestModuleId
  ) {
    return { type: 'external', moduleId: params.sourceModuleId };
  }

  return { type: 'owned' };
}

/**
 * Restituisce gli ID delle sezioni condivise esterne (readonly) presenti
 * nella lista di sezioni.
 */
export function getExternalSectionIds(sections: IEngineSection[]): string[] {
  return sections.filter(s => s.shared?.type === 'external').map(s => s.id);
}

/**
 * In fase di compilazione, il motore di esecuzione dei form valuta le
 * regole di visibilità e abilitazione di ogni sezione e le salva in questa
 * struttura dati.
 */
export interface IEngineRuntimeSection<
  T extends IEngineSection = IEngineSection,
> {
  section: T;
  visible: boolean;
  enabled: boolean;
}
